import { type ReactNode, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LogOut, KeyRound, PenLine, Settings, Mail, Sun, Moon, Search, Command, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from '@/i18n/useTranslations';
import { useTheme } from '@/components/theme-provider';
import { useNavigation } from '@/hooks/use-navigation';
import { useLogout } from '@/hooks/use-logout';
import { useAuthStore } from '@/stores/auth-store';
import { useMessageStore } from '@/stores/message-store';
import { useUiModeStore } from '@/stores/ui-mode-store';
import { useAiChatStore } from '@/stores/ai-chat-store';
import { useShowTryGuidedButton } from '@/hooks/use-guided-ui-mode';
import { useFooterConfig } from '@/hooks/use-footer-config';
import { UserRole } from '@comp-sys/shared';
import { buildBreadcrumbs, stripLocale } from '@/lib/navigation-config';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { LanguageSwitcher } from '@/components/ui/language-switcher';
import { MobileSidebar } from '@/components/layout/mobile-sidebar';
import { BrandLogo } from '@/components/shared/brand-logo';
import { ConfigurableFooter } from '@/components/shared/configurable-footer';
import { ProfileReviewAlert } from '@/components/shared/profile-review-alert';
import { AiChatPanel } from '@/components/ai/ai-chat-panel';
import { AiFloatingButton } from '@/components/ai/ai-floating-button';
import { RoleSwitcherMenuItems } from '@/components/auth/role-switcher';
import { ChangePasswordDialog } from '@/components/users/change-password-dialog';
import { ChangeNameDialog } from '@/components/users/change-name-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/**
 * Refined 家族 shell（flat / professional / minimal / mica）——「企业控制台」。
 *
 * 高密度、数据导向的企业编排，与 glass 的浮空舒朗形成密度反差：
 * - 左侧窄 icon rail（常驻图标 + 悬停 tooltip，无标签），最省横向空间
 * - 持久命令顶栏：面包屑 + 全局搜索（⌘K 提示）+ 角色标签 + 工具
 * - 二级水平 tab 栏（当前栏目子项）
 * - 紧凑内容节奏（p-3/4），数据优先
 *
 * minimal 主题下天然近直角/极细边，配合本 shell 的高密度 = 文档式管理后台。
 */
export function RefinedShell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const pathname = location.pathname;
  const user = useAuthStore((state) => state.user);
  const { navItems } = useNavigation();
  const { config: footerConfig } = useFooterConfig();
  const aiIsOpen = useAiChatStore((s) => s.isOpen);
  const aiIsFloating = useAiChatStore((s) => s.isFloating);
  const isSystemAdmin = user?.activeRole === 'SYSTEM_ADMIN';
  const t = useTranslations('nav');
  const tUser = useTranslations('user');
  const tGlobal = useTranslations();

  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  const isActive = (href: string) =>
    pathname === href || (href !== '/dashboard' && pathname.startsWith(href + '/'));

  const activeTop = navItems.find((i) => isActive(i.href) && i.children?.length);
  const subNav = activeTop?.children ?? [];

  // 面包屑（最后一项标签）
  const breadcrumbs = buildBreadcrumbs(stripLocale(pathname));
  const crumbs = breadcrumbs.map((b) => (b.labelKey ? tGlobal(b.labelKey) : '')).filter(Boolean);

  return (
    <div className="min-h-screen">
      {/* 左侧 icon rail */}
      <IconRail navItems={navItems} isActive={isActive} />

      <div className="md:ml-14 flex items-start min-h-screen">
        <div className="flex-1 min-w-0 flex flex-col min-h-screen">
          {/* 命令顶栏 */}
          <header className="sticky top-0 z-30 h-12 border-b border-border bg-background/95 backdrop-blur-xl">
            <div className="flex h-full items-center gap-3 px-3">
              <div className="md:hidden">
                <MobileSidebar />
              </div>

              {/* 面包屑 */}
              <nav className="hidden sm:flex items-center gap-1.5 text-[13px] min-w-0">
                {crumbs.length > 0 ? (
                  crumbs.map((c, i) => (
                    <span key={i} className="flex items-center gap-1.5 min-w-0">
                      {i > 0 && <span className="text-muted-foreground/40">/</span>}
                      <span
                        className={cn(
                          'truncate',
                          i === crumbs.length - 1 ? 'text-foreground font-medium' : 'text-muted-foreground',
                        )}
                      >
                        {c}
                      </span>
                    </span>
                  ))
                ) : (
                  <span className="text-muted-foreground">控制台</span>
                )}
              </nav>

              {/* 全局搜索 */}
              <div className="hidden lg:flex items-center gap-2 ml-auto mr-1 h-8 px-3 rounded-md border border-border bg-muted/40 text-muted-foreground text-[13px] cursor-text w-56">
                <Search className="h-3.5 w-3.5" />
                <span className="flex-1">搜索…</span>
                <kbd className="flex items-center gap-0.5 text-[10px] px-1 py-0.5 rounded border border-border bg-background">
                  <Command className="h-2.5 w-2.5" />K
                </kbd>
              </div>

              <span className="flex-1 lg:hidden" />

              <div className="flex items-center gap-0.5">
                <span className="hidden md:inline text-[11px] text-muted-foreground px-2 py-0.5 rounded border border-border mr-1">
                  {user ? tUser(`role.${user.activeRole}`) : ''}
                </span>
                <LanguageSwitcher />
                <RefinedUtilities />
                <RefinedUserMenu />
              </div>
            </div>
          </header>

          {/* 二级 tab 栏 */}
          {subNav.length > 0 && (
            <nav className="hidden md:flex items-center gap-1 h-10 border-b border-border px-3 bg-muted/20 overflow-x-auto">
              {subNav.map((child) => {
                const childActive = isActive(child.href);
                return (
                  <Link
                    key={child.href}
                    to={child.href}
                    className={cn(
                      'h-full flex items-center px-3 text-[13px] border-b-2 -mb-px transition-colors whitespace-nowrap',
                      childActive
                        ? 'border-primary text-primary font-medium'
                        : 'border-transparent text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {t(child.labelKey)}
                  </Link>
                );
              })}
            </nav>
          )}

          <main id="main-content" className="flex-1 p-3 md:p-4">
            <ProfileReviewAlert />
            <AnimatePresence>
              <motion.div
                key={pathname}
                initial={prefersReducedMotion ? false : { opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={prefersReducedMotion ? undefined : { opacity: 0, position: 'absolute' }}
                transition={{ duration: 0.12 }}
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </main>

          <ConfigurableFooter variant="dashboard" config={footerConfig} />
        </div>

        {isSystemAdmin && aiIsOpen && !aiIsFloating && <AiChatPanel mode="fixed" />}
      </div>
      {isSystemAdmin && aiIsFloating && <AiChatPanel mode="floating" />}
      {isSystemAdmin && !aiIsOpen && <AiFloatingButton />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 左侧 icon rail                                                      */
/* ------------------------------------------------------------------ */

function IconRail({
  navItems,
  isActive,
}: {
  navItems: ReturnType<typeof useNavigation>['navItems'];
  isActive: (href: string) => boolean;
}) {
  const t = useTranslations('nav');
  return (
    <aside className="fixed left-0 top-0 z-40 h-screen w-14 hidden md:flex flex-col items-center border-r border-border bg-background">
      <div className="h-12 flex items-center justify-center border-b border-border w-full">
        <BrandLogo variant="auto" size="sm" collapsed linkTo="/" />
      </div>
      <nav className="flex-1 overflow-auto py-2 flex flex-col items-center gap-1 w-full">
        {navItems.map((item) => {
          const active = isActive(item.href);
          const Icon = item.icon;
          return (
            <Link
              key={item.href}
              to={item.href}
              title={t(item.labelKey)}
              className={cn(
                'group relative flex items-center justify-center w-10 h-10 rounded-lg transition-colors',
                active
                  ? 'bg-primary/10 text-primary'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted',
              )}
            >
              {active && <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r bg-primary" />}
              <Icon className="h-5 w-5" />
              {/* tooltip */}
              <span className="pointer-events-none absolute left-12 z-50 whitespace-nowrap rounded-md bg-foreground px-2 py-1 text-[12px] text-background opacity-0 group-hover:opacity-100 transition-opacity shadow-md">
                {t(item.labelKey)}
              </span>
            </Link>
          );
        })}
      </nav>
    </aside>
  );
}

/* ------------------------------------------------------------------ */
/* 工具（明暗 + 消息）                                                  */
/* ------------------------------------------------------------------ */

function RefinedUtilities() {
  const { theme, setTheme } = useTheme();
  const navigate = useNavigate();
  const unreadCount = useMessageStore((s) => s.unreadCount);
  const tCommon = useTranslations('common');
  // 引导式回切:资格角色在经典模式下可见,点此回到引导式首页。
  const setUiMode = useUiModeStore((s) => s.setUiMode);
  const guidedEligible = useShowTryGuidedButton();
  const tGuided = useTranslations('guided');
  return (
    <>
      {guidedEligible && (
        <Button
          variant="ghost"
          size="sm"
          className="h-8"
          onClick={() => setUiMode('guided')}
          aria-label={tGuided('switchGuided')}
          title={tGuided('switchGuided')}
        >
          <Sparkles className="h-3.5 w-3.5" />
          <span className="ml-1 hidden lg:inline">{tGuided('switchGuided')}</span>
        </Button>
      )}
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
        aria-label={tCommon('toggleTheme')}
      >
        <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
        <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="relative h-8 w-8"
        onClick={() => navigate('/dashboard/messages')}
        aria-label={tCommon('openMessages')}
      >
        <Mail className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 flex items-center justify-center rounded-full bg-primary text-primary-foreground text-[9px] font-bold px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* 用户菜单                                                            */
/* ------------------------------------------------------------------ */

function RefinedUserMenu() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const { logout, isPending } = useLogout();
  const tAuth = useTranslations('auth');
  const tUsers = useTranslations('users');
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [changeNameOpen, setChangeNameOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const isPrivileged =
    user?.activeRole === UserRole.SYSTEM_ADMIN || user?.activeRole === UserRole.SUPER_ADMIN;

  return (
    <>
      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen}>
        <DropdownMenuTrigger className="rounded-full p-0.5 hover:bg-muted transition-colors cursor-pointer">
          <Avatar className="h-8 w-8 rounded-full border border-border">
            <AvatarImage src="/avatar.png" />
            <AvatarFallback className="rounded-full text-[11px]">
              {user?.displayName?.[0]?.toUpperCase() || 'U'}
            </AvatarFallback>
          </Avatar>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <div className="px-2 py-1.5">
            <p className="text-sm font-medium truncate">{user?.displayName || user?.email}</p>
            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
          </div>
          <DropdownMenuSeparator />
          <RoleSwitcherMenuItems onSwitchSuccess={() => setMenuOpen(false)} />
          <DropdownMenuItem onClick={() => setChangeNameOpen(true)}>
            <PenLine className="h-4 w-4" />
            {tUsers('changeName')}
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => setChangePasswordOpen(true)}>
            <KeyRound className="h-4 w-4" />
            {tUsers('changePassword')}
          </DropdownMenuItem>
          {isPrivileged && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/dashboard/settings')}>
                <Settings className="h-4 w-4" />
                {tAuth('logout.settings')}
              </DropdownMenuItem>
            </>
          )}
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={logout} disabled={isPending} className="text-error-text">
            <LogOut className="h-4 w-4" />
            {tAuth('logout.title')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <ChangePasswordDialog open={changePasswordOpen} onOpenChange={setChangePasswordOpen} />
      <ChangeNameDialog open={changeNameOpen} onOpenChange={setChangeNameOpen} />
    </>
  );
}
